/**
 * MaterialFieldButton - Material/mix field button using existing hooks
 */

import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FieldButton} from '../../FieldButton'

interface MaterialFieldButtonProps {
  onClick: () => void
  requiresApproval?: boolean
  isReadOnly?: boolean
  isChanged?: boolean
}

export const MaterialFieldButton: React.FC<MaterialFieldButtonProps> = ({
  onClick,
  requiresApproval = false,
  isReadOnly = false,
  isChanged = false
}) => {
  const {t} = useTranslation()
  const {
    selectedMaterialDisplay,
    selectedMaterialVariations,
    selectedMaterialHasVariations,
    defaultMaterial,
    defaultMaterialVariations
  } = useMix()

  // Check if we're still on the original material from the order
  const isOnDefaultMaterial = selectedMaterialDisplay?.materialId === defaultMaterial?.materialId

  // Use current selection, fallback to default values ONLY if still on default material
  // Only show variations if the selected material supports them
  const materialVariationsLine = useMemo(() => {
    if (!selectedMaterialHasVariations) return null
    // Only fallback to default variations if we're still on the default material
    const variations =
      selectedMaterialVariations ?? (isOnDefaultMaterial ? defaultMaterialVariations : null)
    if (!variations) return null
    const parts: string[] = []
    const slumpValue = variations.slump?.description || variations.slump?.value
    if (slumpValue) {
      parts.push(`${slumpValue} Slump`)
    }
    const setControlText = variations.setControl?.description || variations.setControl?.value
    if (setControlText) {
      parts.push(setControlText)
    }
    return parts.length > 0 ? `*with ${parts.join('; ')}` : null
  }, [
    selectedMaterialHasVariations,
    selectedMaterialVariations,
    isOnDefaultMaterial,
    defaultMaterialVariations
  ])

  // Combine material name (single line) and variations (next line) with required asterisks when changed
  const displayValue = useMemo(() => {
    const materialName = selectedMaterialDisplay?.materialDescription || null
    if (!materialName) return null

    // Only add asterisk when changed AND material has variations set
    const materialLine = isChanged && materialVariationsLine ? `${materialName}*` : materialName
    if (!materialVariationsLine) return materialLine
    // Force variations onto the next line to match the design (material on first line, variations on second)
    return [materialLine, materialVariationsLine].join('\n')
  }, [selectedMaterialDisplay, materialVariationsLine, isChanged])

  return (
    <FieldButton
      label={t('orderIntake.rmc.orderManagement.mixType', 'Mix type')}
      value={displayValue}
      onClick={onClick}
      requiresApproval={requiresApproval}
      isReadOnly={isReadOnly}
      isChanged={isChanged}
    />
  )
}
